import { Anchor, Badge, Box, Button, Grid, Group, SimpleGrid, Stack, Tabs, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DisabledReason, getModuleUrl, HelpButtonGroup, i18n, useHelpContext, useTenant } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, WarehouseIcon } from "@phosphor-icons/react";
import { JSX, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi, TrustScoreApi } from "../../../apis";
import type { IReturnItem } from "../../../apis/return";
import { PageLayout } from "../../../components/layout";
import { ReturnActionBar } from "../../../components/return/action-bar";
import { ReturnStatusBadge, reasonLabel, subReasonLabel } from "../../../components/return/atoms";
import { DetailCard, Field, SideRow } from "../../../components/return/detail-card";
import { InventoryImpactModal } from "../../../components/return/inventory-impact-modal";
import { ReturnItemsTable } from "../../../components/return/items-table";
import { returnTypeLabel } from "../../../components/return/list-table";
import { useReturnActions } from "../../../components/return/return-actions";
import { ReturnTimeline, actionLabel } from "../../../components/return/timeline";
import { ReturnCommentThread } from "../../../components/return/comment-thread";
import { ReturnAuditExport } from "../../../components/return/audit-export";
import { ReturnWorkflowStrip } from "../../../components/return/workflow-strip";
import { ReturnShippingPanel } from "../../../components/return/shipping-panel";
import { ReturnResolutionPanel } from "../../../components/return/resolution-panel";
import { TrustBadge, TrustSummaryLine } from "../../../components/return/trust-badge";
import { ReturnInsightsPanel } from "../../../components/return/insights-panel";
import { DotPill } from "../../../components/delivery/atoms";
import { DeliveryActionButton } from "../../../components/delivery/action-controls";
import { ListState } from "../../../components/ui";
import { IAuth } from "../../../store/Auth";
import { currencyFormat } from "../../../utils/currency";
import { dateFormat } from "../../../utils/date";

const shippingLabel = (r?: string | null) => (r ? i18n.t(`returns:shipping.responsibility.${r}`, { defaultValue: "-" }) : "-");
const refundMethodLabel = (m?: string | null) => (m ? i18n.t(`returns:refundMethod.${m}`, { defaultValue: "-" }) : "-");
const currencyLabel = (c?: string | null) => (c ? i18n.t(`returns:detail.currencyName.${c}`, { defaultValue: c }) : "-");

/** Row «→» actions that live on a tab rather than in a dialog. */
const TAB_FOR_ACTION: Record<string, string> = {
  schedulePickup: "shipping",
  markShipped   : "shipping",
  reportTransit : "shipping",
  resolveTransit: "shipping"
};

// §9.2 «Өмнөх буцаалт: N (X% буцаалтын хувь)». Same SWR key as TrustBadge, so no extra request.
function BuyerReturnRate ({ businessId }: { businessId: string }){
  const { t } = useTranslation("returns");
  const { data } = useSWR(["trust-score.get", businessId], () => TrustScoreApi.get(businessId), { revalidateOnFocus: false });
  if (!data) return null;
  return (
    <Field
      label={t("detail.priorReturns")}
      value={t("detail.priorReturnsValue", { count: data.totalReturns, pct: Number(data.returnRatePercent).toFixed(1) })}
    />
  );
}

/** A linked document: its number when the API gives one, otherwise «Нээх» (never a raw id). */
function DocLink ({ label, href, to }: { label?: string | null; href?: string; to?: string }){
  const { t } = useTranslation("returns");
  const text = label || t("detail.openLink");
  if (to) return <Anchor component={Link} to={to} ff={label ? "monospace" : undefined} fz="var(--nw-fz-body)">{text}</Anchor>;
  if (href) return <Anchor href={href} ff={label ? "monospace" : undefined} fz="var(--nw-fz-body)">{text}</Anchor>;
  return <>{label || "-"}</>;
}

function TabCount ({ n }: { n: number }){
  return <Badge size="md" variant="light" color="gray" radius="xl">{n}</Badge>;
}

export function ReturnDetailPage (){
  const { t } = useTranslation("returns");
  const { id = "" } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  // Captured once: the history state is cleared below so a reload does not reopen the dialog.
  const [openAction] = useState<string | null>(() => (location.state as { openAction?: string } | null)?.openAction ?? null);
  const [allocateOpen, allocateHandlers] = useDisclosure(false);
  const [allocateItem, setAllocateItem] = useState<IReturnItem | null>(null);
  const [tab, setTab] = useState<string>(openAction && TAB_FOR_ACTION[openAction] ? TAB_FOR_ACTION[openAction] : "overview");
  const accessToken = useSelector((state: { auth: IAuth }) => state.auth.accessToken);
  const tenant = useTenant();
  const { stateOf } = useReturnActions();
  const m = (mod: string) => getModuleUrl(mod, tenant.modulesDomain);
  const crossAdmin = (mod: string, redirectPath: string) =>
    `${m(mod)}/access?token=${accessToken ?? ""}&redirect=${encodeURIComponent(redirectPath)}`;

  const { data, error, isLoading, mutate } = useSWR(
    id ? ["returns.get", id] : null,
    () => ReturnApi.get(id),
    { revalidateOnFocus: false }
  );

  // @dehub/help: the return status drives the drawer's «Одоо» step (R15 flow) and the status tab.
  const { set: setHelpContext } = useHelpContext();
  const helpStatus = data?.returnRequest?.status;
  useEffect(() => {
    setHelpContext({ status: helpStatus ?? undefined });
  }, [helpStatus, setHelpContext]);

  // Consume the navigation state once so a reload does not reopen the dialog.
  useEffect(() => {
    if (openAction) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ret = data?.returnRequest;
  const allocateState = ret ? stateOf("inventoryImpact", ret) : null;
  const openAllocate = (item: IReturnItem) => {
    setAllocateItem(item);
    allocateHandlers.open();
  };

  const breadcrumb = [
    { to: "/", label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { label: ret?.returnNumber ?? "-" }
  ];

  const title: string | JSX.Element = ret ? (
    <Group gap="sm" wrap="wrap" align="center">
      <Text span ff="monospace" fz="var(--nw-fz-title)" fw={700} translate="no">{ret.returnNumber}</Text>
      <ReturnStatusBadge status={ret.status} help />
    </Group>
  ) : t("breadcrumb.returns");

  const subTitle = ret
    ? [
      returnTypeLabel(ret),
      t("detail.versionN", { n: ret.version }),
      ret.orderNumber ? t("detail.orderN", { number: ret.orderNumber }) : null
    ].filter(Boolean).join(" · ")
    : undefined;

  const recent = data ? [...data.timeline].slice(-3).reverse() : [];

  return (
    <PageLayout
      title={title}
      subTitle={subTitle}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={[
        <Button key="back" variant="default" leftSection={<ArrowLeftIcon size={18} />} onClick={() => navigate("/returns")}>
          {t("detail.back")}
        </Button>
      ]}
    >
      {error && !data ? (
        <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
      ) : null}

      {data && ret ? (
        <Stack gap="md">
          <ReturnWorkflowStrip status={ret.status} />

          {ret.status === "AWAITING_WAREHOUSE_RECEIPT" ? (
            <DetailCard>
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Stack gap={2}>
                  <Group gap="xs"><DotPill tone="amber">{t("detail.warehousePendingTitle")}</DotPill></Group>
                  <Text size="sm" c="var(--nw-text-2)">{t("detail.warehousePendingText")}</Text>
                </Stack>
                <DisabledReason
                  helpKey="order.returns-detail.reason.warehouse-receive"
                  reason={stateOf("warehouseReceive", ret).code}
                  when={!stateOf("warehouseReceive", ret).enabled}
                  position="bottom-end"
                >
                  <HelpButtonGroup helpKey="order.returns-detail.action.warehouse-receive">
                    <DeliveryActionButton
                      state={{ allowed: stateOf("warehouseReceive", ret).enabled, reason: stateOf("warehouseReceive", ret).reason }}
                      leftSection={<WarehouseIcon size={18} />}
                      variant="default"
                      onClick={() => navigate(`/returns/${ret.id}/warehouse-receipt`)}
                    >
                      {t("detail.warehouseConfirmCta")}
                    </DeliveryActionButton>
                  </HelpButtonGroup>
                </DisabledReason>
              </Group>
            </DetailCard>
          ) : null}

          <DetailCard>
            <ReturnActionBar detail={ret} onChange={() => mutate()} initialAction={TAB_FOR_ACTION[openAction ?? ""] ? null : openAction} />
          </DetailCard>

          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Tabs
                value={tab}
                onChange={(v) => setTab(v ?? "overview")}
                styles={{ list: { flexWrap: "nowrap" }, tab: { paddingInline: "var(--mantine-spacing-sm)" } }}
              >
                <Tabs.List mb="md">
                  <Tabs.Tab value="overview">{t("detail.tab.overview")}</Tabs.Tab>
                  <Tabs.Tab value="items" rightSection={<TabCount n={data.items.length} />}>{t("detail.tab.items")}</Tabs.Tab>
                  <Tabs.Tab value="timeline" rightSection={<TabCount n={data.timeline.length} />}>{t("detail.tab.timeline")}</Tabs.Tab>
                  <Tabs.Tab value="resolution">{t("detail.tab.resolution")}</Tabs.Tab>
                  <Tabs.Tab value="shipping">{t("detail.tab.shipping")}</Tabs.Tab>
                  <Tabs.Tab value="damaged" rightSection={<TabCount n={data.damagedInventory.length} />}>{t("detail.tab.damaged")}</Tabs.Tab>
                  <Tabs.Tab value="insights">{t("detail.tab.insights")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview">
                  <Stack gap="md">
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <DetailCard title={t("detail.section.buyer")} helpKey="order.returns-detail.section.buyer">
                        <SimpleGrid cols={2} spacing="md" verticalSpacing="sm">
                          <Field label={t("detail.field.business")} value={ret.buyerBusinessName} />
                          <Field label={t("detail.field.ref")} value={ret.buyerBusinessRefCode} mono />
                          <Stack gap={4}>
                            <Text size="sm" c="var(--nw-text-2)">{t("detail.field.trust")}</Text>
                            {ret.buyerBusinessId ? (
                              <>
                                <Box><TrustBadge businessId={ret.buyerBusinessId} /></Box>
                                <TrustSummaryLine businessId={ret.buyerBusinessId} />
                              </>
                            ) : <Text size="sm">-</Text>}
                          </Stack>
                          <Field label={t("detail.field.owner")} value={ret.createdByName} />
                          {ret.buyerBusinessId ? <BuyerReturnRate businessId={ret.buyerBusinessId} /> : null}
                        </SimpleGrid>
                      </DetailCard>
                      <DetailCard title={t("detail.section.seller")} helpKey="order.returns-detail.section.seller">
                        <SimpleGrid cols={2} spacing="md" verticalSpacing="sm">
                          <Field label={t("detail.field.business")} value={ret.sellerBusinessName} />
                          <Field label={t("detail.field.ref")} value={ret.sellerBusinessRefCode} mono />
                          <Field label={t("detail.field.owner")} value={ret.proposedByName ?? ret.acceptedByName} />
                        </SimpleGrid>
                      </DetailCard>
                    </SimpleGrid>

                    <DetailCard title={t("detail.section.orderInfo")} helpKey="order.returns-detail.section.order-info">
                      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                        <Field label={t("detail.field.order")} mono={!!ret.orderNumber}
                          value={ret.orderId ? <DocLink label={ret.orderNumber} to={`/buyer-sales/detail/${ret.orderId}`} /> : null} />
                        <Field label={t("detail.field.delivery")}
                          value={ret.deliveryId ? <DocLink to="/active-deliveries" /> : null} />
                        <Field label={t("detail.field.invoice")} mono={!!ret.invoiceNumber}
                          value={ret.invoiceId ? <DocLink label={ret.invoiceNumber} href={crossAdmin("invoice", `/sales-invoice/${ret.invoiceId}/details`)} /> : null} />
                        <Field label={t("detail.field.creditNote")} mono
                          value={ret.creditNoteId ? <DocLink label={ret.creditNoteNumber} href={crossAdmin("invoice", `/refund-invoice/${ret.creditNoteId}`)} /> : null} />
                      </SimpleGrid>
                    </DetailCard>

                    <DetailCard title={t("detail.section.reasonAmount")} helpKey="order.returns-detail.section.reason-amount">
                      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                        <Field label={t("detail.section.reason")} value={reasonLabel(ret.returnReason)} />
                        <Field label={t("detail.field.totalAmount")} mono value={currencyFormat(ret.totalRequestedAmount ?? 0)} />
                        <Field label={t("detail.field.refundPercent")} mono value={ret.refundPercent != null ? `${ret.refundPercent}%` : null} />
                        <Field label={t("detail.field.currency")} value={currencyLabel(ret.currency)} />
                      </SimpleGrid>
                      {ret.subReason || ret.reasonDetails ? (
                        <Stack gap={4} mt="md">
                          {ret.subReason ? <Text size="sm">{t("detail.subReasonPrefix")}{subReasonLabel(ret.subReason)}</Text> : null}
                          {ret.reasonDetails ? (
                            <Text size="sm"><Text span c="var(--nw-text-2)">{t("detail.buyerNote")}</Text> {ret.reasonDetails}</Text>
                          ) : null}
                        </Stack>
                      ) : null}
                    </DetailCard>

                    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                      <DetailCard title={t("detail.section.buyerChoice")} helpKey="order.returns-detail.section.buyer-choice">
                        <Stack gap="sm">
                          <Field label={t("detail.field.preferred")} value={refundMethodLabel(ret.preferredRefundMethod)} />
                          <Text size="sm" c="var(--nw-text-2)">
                            {ret.buyerOverrideAllowed ? t("detail.overrideAllowedHint") : t("detail.overrideNotAllowed")}
                          </Text>
                        </Stack>
                      </DetailCard>
                      <DetailCard title={t("detail.section.targetWarehouse")} helpKey="order.returns-detail.section.target-warehouse">
                        <Stack gap="sm">
                          <Field label={t("detail.field.warehouse")} value={ret.targetWarehouseName} />
                          <Text size="sm" c="var(--nw-text-2)">{t("detail.warehouseOverrideHint")}</Text>
                        </Stack>
                      </DetailCard>
                      <DetailCard title={t("detail.section.shippingChannel")} helpKey="order.returns-detail.section.shipping-channel">
                        <Stack gap="sm">
                          <Field label={t("detail.field.responsibility")} value={ret.shippingResponsibility ? shippingLabel(ret.shippingResponsibility) : null} />
                          <Field label={t("detail.field.pickupSla")} value={t("detail.daysValue", { count: ret.pickupSlaDays ?? 14 })} />
                        </Stack>
                      </DetailCard>
                    </SimpleGrid>

                    {ret.isOffSystemBuyerClaim ? (
                      <DetailCard title={t("detail.section.offSystemClaim")} helpKey="order.returns-detail.section.off-system-claim">
                        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                          <Field label={t("detail.field.proofOfPurchase")} value={ret.proofOfPurchase} />
                          <Field label={t("detail.field.date")} mono value={ret.proofOfPurchaseDate ? dateFormat(ret.proofOfPurchaseDate) : null} />
                          <Field label={t("detail.field.verification")}
                            value={ret.sellerVerificationStatus ? t(`verify.${ret.sellerVerificationStatus}`, { defaultValue: "-" }) : null} />
                        </SimpleGrid>
                        {ret.sellerVerificationNotes ? <Text size="sm" c="var(--nw-text-2)" mt="sm">{ret.sellerVerificationNotes}</Text> : null}
                      </DetailCard>
                    ) : null}
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="items">
                  <ReturnItemsTable
                    items={data.items}
                    currency={ret.currency}
                    damagedInventory={data.damagedInventory}
                    onAllocate={openAllocate}
                    canAllocate={!!allocateState?.applies}
                    allocateReason={allocateState && !allocateState.enabled ? allocateState.reason : null}
                    allocateCode={allocateState && !allocateState.enabled ? allocateState.code : null}
                    returnRequestId={ret.id}
                    parentResolutionType={ret.resolutionType ?? null}
                    parentStatus={ret.status}
                    onItemUpdated={() => mutate()}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="timeline">
                  <Stack gap="md">
                    <DetailCard title={t("detail.timeline.auditTitle")} helpKey="order.returns-detail.section.audit">
                      <Text size="sm" c="var(--nw-text-2)" mb="sm">{t("detail.timeline.auditCaption")}</Text>
                      <ReturnTimeline entries={data.timeline} pendingSteps={data.returnRequest.pendingSteps} />
                    </DetailCard>
                    <DetailCard title={t("detail.timeline.notesTitle")} helpKey="order.returns-detail.section.notes">
                      <Text size="sm" c="var(--nw-text-2)" mb="sm">{t("detail.timeline.notesCaption")}</Text>
                      <ReturnCommentThread returnId={id} />
                    </DetailCard>
                    <DetailCard title={t("detail.timeline.exportTitle")}>
                      <Text size="sm" c="var(--nw-text-2)" mb="sm">{t("detail.timeline.exportCaption")}</Text>
                      <ReturnAuditExport returnId={id} detail={data} />
                    </DetailCard>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="resolution">
                  <ReturnResolutionPanel detail={ret} onChange={() => mutate()} />
                </Tabs.Panel>

                <Tabs.Panel value="shipping">
                  <ReturnShippingPanel detail={ret} onChange={() => mutate()} />
                </Tabs.Panel>

                <Tabs.Panel value="damaged">
                  {data.damagedInventory.length === 0 ? (
                    <DetailCard>
                      <ListState state="empty" title={t("detail.damagedEmptyTitle")} description={t("detail.damagedEmptyText")} />
                    </DetailCard>
                  ) : (
                    <Stack gap="sm">
                      {data.damagedInventory.map((d) => (
                        <DetailCard key={d.id}>
                          <Group justify="space-between" wrap="wrap" gap="sm">
                            <Stack gap={2}>
                              <Text size="sm" fw={600}>
                                {t(`inventoryImpact.bucket.${d.inventoryImpact}`, { defaultValue: "-" })} · {t("detail.qtyPieces", { count: d.quantity })}
                              </Text>
                              <Text size="sm" c="var(--nw-text-2)" ff="monospace">{d.bucketLocationCode || "-"}</Text>
                            </Stack>
                            {d.disposedAt ? (
                              <DotPill tone="green">
                                {t("detail.disposed")} · {d.finalDisposition ? t(`inbound:damaged.disposition.${d.finalDisposition}`, { defaultValue: "-" }) : "-"}
                              </DotPill>
                            ) : null}
                          </Group>
                        </DetailCard>
                      ))}
                    </Stack>
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="insights">
                  <ReturnInsightsPanel ret={ret} items={data.items} />
                </Tabs.Panel>
              </Tabs>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <DetailCard title={t("detail.recentActions")} helpKey="order.returns-detail.section.recent-actions">
                  {recent.length === 0 ? (
                    <Text size="sm" c="var(--nw-text-2)">{t("detail.noRecords")}</Text>
                  ) : (
                    <Stack gap={0}>
                      {recent.map((e, i) => (
                        <SideRow key={`${e.action}-${e.timestamp}-${i}`} label={actionLabel(e.action)} value={dateFormat(e.timestamp)} mono />
                      ))}
                    </Stack>
                  )}
                  <Anchor component="button" type="button" fw={600} fz="var(--nw-fz-body)" c="var(--nw-text)" mt="sm" onClick={() => setTab("timeline")}>
                    {t("detail.viewAll")}
                  </Anchor>
                </DetailCard>

                {ret.slaDeadline ? (
                  <DetailCard title={t("detail.slaMonitor")} helpKey="order.returns-detail.section.sla">
                    <SideRow
                      label={ret.status === "AWAITING_WAREHOUSE_RECEIPT" ? t("detail.slaWarehouse") : t("detail.slaPickup", { count: ret.pickupSlaDays ?? 14 })}
                      value={ret.slaDaysRemaining != null && ret.slaDaysRemaining > 0
                        ? t("detail.daysRemaining", { count: ret.slaDaysRemaining })
                        : <DotPill tone="red">{t("detail.daysOverdue", { count: Math.abs(ret.slaDaysRemaining ?? 0) })}</DotPill>}
                    />
                  </DetailCard>
                ) : null}

                <DetailCard title={t("detail.section.buyer")}>
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <Text size="sm">{ret.buyerBusinessName || "-"}</Text>
                    {ret.buyerBusinessId ? <TrustBadge businessId={ret.buyerBusinessId} /> : <Text size="sm">-</Text>}
                  </Group>
                </DetailCard>

                <DetailCard title={t("detail.crossLink")} helpKey="order.returns-detail.section.cross-link">
                  <Stack gap={0}>
                    <SideRow label={t("detail.field.order")} mono
                      value={ret.orderId ? <DocLink label={ret.orderNumber} to={`/buyer-sales/detail/${ret.orderId}`} /> : null} />
                    <SideRow label={t("detail.field.invoice")} mono
                      value={ret.invoiceId ? <DocLink label={ret.invoiceNumber} href={crossAdmin("invoice", `/sales-invoice/${ret.invoiceId}/details`)} /> : null} />
                    <SideRow label={t("detail.field.delivery")}
                      value={ret.deliveryId ? <DocLink to="/active-deliveries" /> : null} />
                    {ret.replacementOrderId ? (
                      <SideRow label={t("detail.field.replacementOrder")} value={<DocLink to={`/buyer-sales/detail/${ret.replacementOrderId}`} />} />
                    ) : null}
                    {ret.creditNoteId ? (
                      <SideRow label={t("detail.field.creditNote")} mono
                        value={<DocLink label={ret.creditNoteNumber} href={crossAdmin("invoice", `/refund-invoice/${ret.creditNoteId}`)} />} />
                    ) : null}
                    {ret.refundSettlementId ? (
                      <SideRow label={t("detail.field.settlement")} value={<DocLink href={crossAdmin("invoice", `/settlement/${ret.refundSettlementId}`)} />} />
                    ) : null}
                    {ret.consignmentContractId ? (
                      <SideRow label={t("detail.field.contract")} value={<DocLink href={crossAdmin("consignment", `/agreements/${ret.consignmentContractId}`)} />} />
                    ) : null}
                    {ret.ebarimtReturnReceiptId ? (
                      <SideRow label={t("detail.field.ebarimt")} value={t("detail.ebarimtIssued")} />
                    ) : null}
                  </Stack>
                </DetailCard>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      ) : null}

      <InventoryImpactModal
        opened={allocateOpen}
        onClose={allocateHandlers.close}
        returnRequestId={id}
        item={allocateItem}
        damagedInventory={data?.damagedInventory ?? []}
        onSaved={() => mutate()}
      />
    </PageLayout>
  );
}
